import { CSSProperties, memo, useMemo } from "react";
import {
  Form,
  FormField,
  FormGroup,
  Header,
  Modal,
  ModalContent,
  Label,
  Button,
} from "semantic-ui-react";

// Components
import { Table } from "react-lib/frameworks/Table";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { BillingDetailType } from "./sequence/PaymentSummary";
import { formatComma } from "react-lib/utils";
import { SelectedCustomerData } from "./types/MeBillingType";

type ModPaymentDetailProps = {
  open: boolean;
  onClose: () => void;
  // data
  paymentDetail?: BillingDetailType;
  selectedCustomer?: SelectedCustomerData;
};

// Styles
const STYLES: Record<string, CSSProperties> = {
  labelStyle: {
    fontSize: "14px",
    fontWeight: 500,
  },
  valueStyle: {
    fontSize: "14px",
    fontWeight: "bold",
  },
  tableHeader: {
    backgroundColor: "rgba(255, 200, 200, 0.5)",
    color: "#333",
    fontWeight: 500,
  },
};

const ModPaymentDetail = (props: ModPaymentDetailProps) => {
  const paymentDetail = props.paymentDetail?.detail;
  const paymentItem = props.paymentDetail?.item;
  const proxyPatientAdmin = props.selectedCustomer?.proxyPatientAdmin;
  const patientDetail = props.selectedCustomer?.patientDetail;

  const formattedDatetime = useMemo(() => {
    if (!paymentDetail?.created) return "-";

    return formatISOtoBEDateTime(paymentDetail?.created).dateTimeShortBracket;
  }, [paymentDetail?.created]);

  const flex =
    (justify: React.CSSProperties["justifyContent"] = "center") =>
    (children: React.ReactNode) =>
      <div style={{ display: "flex", justifyContent: justify }}>{children}</div>;

  const formattedItems = useMemo(() => {
    return (paymentItem || []).map((item) => {
      const total = Number(item?.price ?? 0);
      const discount = Number(item?.discount ?? 0);
      const amount = total - discount;

      return {
        ...item,
        created: flex()(formatISOtoBEDateTime(item?.created)?.date),
        quantity: flex("flex-end")(item?.quantity),
        unitprice: flex("flex-end")(item?.unitprice),
        price: flex("flex-end")(item?.price),
        amount: flex("flex-end")(formatComma(String(amount))),
      };
    });
  }, [paymentItem]);

  const formattedTotalPrice = useMemo(() => {
    const sumServiceOrder = (paymentItem ?? []).reduce(
      (acc, item) => acc + Number(item.price || 0),
      0
    );

    return formatComma(sumServiceOrder);
  }, [paymentItem]);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <ModalContent style={{ padding: "1rem" }}>
        <Form style={{ width: "100%" }}>
          <FormGroup
            inline
            style={{ justifyContent: "space-between", alignItems: "end", marginBottom: "0.5rem" }}
          >
            <FormField>
              <Header as="h2" style={{ color: "var(--primary-theme-color)", margin: 0 }}>
                รายละเอียดค่าใช้จ่าย
              </Header>
            </FormField>
            <FormField>
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                [{patientDetail?.id}] {patientDetail?.full_name}
              </span>
            </FormField>
          </FormGroup>
        </Form>

        <div style={{ borderTop: "solid 2px var(--primary-theme-color)", marginBottom: "1rem" }} />

        <Form style={{ marginBottom: "1rem" }}>
          <FormGroup inline>
            <FormField>
              <span style={STYLES.labelStyle}>หมายเลขรับบริการ:</span>
            </FormField>
            <FormField>
              <span style={STYLES.valueStyle}>{paymentDetail?.number}</span>
            </FormField>
            <FormField style={{ marginLeft: "1rem" }}>
              <span style={STYLES.labelStyle}>วันที่รับบริการ:</span>
            </FormField>
            <FormField>
              <span style={STYLES.valueStyle}>{formattedDatetime}</span>
            </FormField>
            <FormField style={{ marginLeft: "1rem" }}>
              <span style={STYLES.labelStyle}>ผู้ให้บริการ:</span>
            </FormField>
            <FormField>
              <span style={STYLES.valueStyle}>
                {"-"} [{proxyPatientAdmin?.division?.name}]
              </span>
            </FormField>
          </FormGroup>
        </Form>

        <Table
          data={formattedItems}
          headers="รหัส, รายการค่าบริการ, วันที่เวลาค่าใช้จ่าย, จำนวน, ราคาต่อหน่วย, ราคารวม, ยอดชำระ"
          keys="code, product, created, quantity, unitprice, price, amount"
          showPagination={false}
          style={{ height: "300px" }}
          widths="100, ^200, 130, 80, 120, 120, 120"
        />

        <Form style={{ marginTop: "1rem" }}>
          <FormGroup inline style={{ justifyContent: "space-between", alignItems: "center" }}>
            {/* Total */}
            <FormField>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>ยอดรวมทั้งหมด</span>
                <Label
                  basic
                  style={{
                    backgroundColor: "#f5f5f5",
                    fontSize: "14px",
                    fontWeight: "bold",
                    minWidth: "120px",
                    textAlign: "center",
                  }}
                >
                  {formattedTotalPrice}
                </Label>
              </div>
            </FormField>

            {/* Close Button */}
            <FormField style={{ paddingRight: 0 }}>
              <Button
                onClick={props.onClose}
                style={{
                  minWidth: "120px",
                  borderRadius: "8px",
                }}
              >
                ปิดหน้าต่าง
              </Button>
            </FormField>
          </FormGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
};

ModPaymentDetail.displayName = "ModPaymentDetail";

export default memo(ModPaymentDetail);
